import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { getGetStartedChecklist } from "../../services/onboarding";
import { subscribeToGetStartedChanges } from "../../services/onboardingEvents";
import type { GetStartedChecklist } from "../../types/onboarding";

/**
 * Options for {@link useGetStartedChecklist}.
 *
 * **Primitives only.** Every field here is destructured to a value that goes straight into the
 * effect's dependency array, and both callers pass a fresh object literal on every render — an
 * object or array option would compare unequal each time and refetch in a loop.
 */
export interface UseGetStartedChecklistOptions {
  /**
   * Ask the server for the completion set even when this user has hidden the panel.
   *
   * Leave it off unless the caller renders the items in that state. See the service, which explains
   * what the default saves on the landing route.
   */
  includeItems?: boolean;
}

/**
 * Where the read stands, for the callers that need more than "something or nothing".
 *
 * - `loading`: no answer yet.
 * - `ready`: `checklist` holds the server's answer.
 * - `unavailable`: this API has no checklist (the service reads a 404 as null). Nothing will change
 *   that for the life of the tab, so a caller can hide its way in.
 * - `error`: the read failed. Already reported to Sentry. Not cached, so the next mount retries.
 */
export type GetStartedChecklistStatus = "loading" | "ready" | "unavailable" | "error";

/**
 * Reads the caller's Get Started checklist once, and hands back a setter that is safe to call after
 * an await.
 *
 * Shared by the panel and by the settings card that reverses a dismissal, which otherwise carried
 * the same effect, the same teardown and the same swallow-and-render-nothing catch twice.
 *
 * `checklist` is null both while the read is in flight and when there is nothing to show, because
 * the two look identical to a caller: this panel is decoration on pages that work without it, so
 * neither state renders a spinner or an error at a coach who did not ask for one.
 */
export const useGetStartedChecklist = ({
  includeItems = false,
}: UseGetStartedChecklistOptions = {}) => {
  const [checklist, setChecklist] = useState<GetStartedChecklist | null>(null);
  const [status, setStatus] = useState<GetStartedChecklistStatus>("loading");
  // Bumped when a dismiss or restore anywhere in the tab changes the answer, so a copy that is
  // already mounted reads it again instead of holding the old one until the next navigation.
  const [revision, setRevision] = useState(0);
  // Tracks whether this component is still mounted, so nothing writes state into a render that no
  // longer exists. React 19 dropped the warning for that, so it now fails silently.
  const activeRef = useRef(true);

  useEffect(
    () => subscribeToGetStartedChanges(() => setRevision((current) => current + 1)),
    []
  );

  useEffect(() => {
    activeRef.current = true;
    const controller = new AbortController();

    getGetStartedChecklist({ includeItems, signal: controller.signal })
      .then((result) => {
        // This run's signal as well as the mounted flag: a refetch replaces this run while the
        // component stays mounted, and an answer that lands after that must not overwrite the
        // newer one.
        if (controller.signal.aborted || !activeRef.current) return;
        setChecklist(result);
        setStatus(result ? "ready" : "unavailable");
      })
      .catch((error) => {
        // An abort fired by the cleanup below is an ordinary unmount, not an outage, and the API
        // client reports it as a 408 that is indistinguishable from a real timeout, so the signal
        // is the only thing that can tell the two apart.
        //
        // It has to be THIS run's signal, not the mounted flag: StrictMode mounts, cleans up and
        // mounts again synchronously, so `activeRef` is already back to true by the time the first
        // run's abort rejection lands, and every dev page load would report a phantom timeout.
        if (controller.signal.aborted) return;

        // An older API is already absorbed in the service, which reads a 404 as "no such feature"
        // and resolves null. Anything still arriving here is a real failure, so while the coach
        // correctly sees nothing, it must not also be invisible to us.
        Sentry.captureException(error, {
          tags: { feature: "onboarding" },
          extra: { endpoint: "/api/onboarding/checklist" },
        });
        // A refetch that fails keeps the answer it already had on screen; only a first read that
        // fails has nothing to show.
        if (activeRef.current) setStatus((current) => (current === "ready" ? current : "error"));
      });

    return () => {
      activeRef.current = false;
      controller.abort();
    };
  }, [includeItems, revision]);

  /** `setChecklist` that no-ops once the component has gone away. */
  const setIfActive = useCallback((next: GetStartedChecklist) => {
    if (!activeRef.current) return;
    setChecklist(next);
    setStatus("ready");
  }, []);

  return { checklist, status, setChecklist: setIfActive, activeRef };
};
